import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Clock, Warning } from '@phosphor-icons/react';
import { LoadingSpinner } from '../components/ui/loading-spinner';
import { toast } from 'sonner';

import { getApiBaseUrl } from '../utils/api';
export function PaymentSuccess() {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [attempts, setAttempts] = useState(0);
  
  const checkPaymentStatus = React.useCallback(async (sessionId, attempt = 0) => {
    const maxAttempts = 10;
    
    if (attempt >= maxAttempts) {
      setPaymentStatus({
        status: 'timeout',
        message: 'Payment verification timed out. Please check your registrations.'
      });
      setLoading(false);
      return;
    }
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/payments/status/${sessionId}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.payment_status === 'paid') {
          setPaymentStatus({
            status: 'success',
            message: 'Payment successful! Your registration is confirmed.',
            amount: data.amount_total ? (data.amount_total / 100).toFixed(2) : '0.00'
          });
          setLoading(false);
          toast.success('Payment completed successfully!');
        } else if (data.status === 'expired') {
          setPaymentStatus({
            status: 'expired',
            message: 'Payment session expired. Please try registering again.'
          });
          setLoading(false);
        } else {
          // Still processing, try again
          setAttempts(attempt + 1);
          setTimeout(() => checkPaymentStatus(sessionId, attempt + 1), 2000);
        }
      } else {
        throw new Error('Failed to check payment status');
      }
    } catch (error) {
      console.error('Payment status check error:', error);
      setPaymentStatus({
        status: 'error',
        message: 'Unable to verify payment status. Please contact support if you were charged.'
      });
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId) {
      checkPaymentStatus(sessionId);
    } else {
      setLoading(false);
      toast.error('No payment session found');
    }
  }, [location.search, checkPaymentStatus]);
  
  const getStatusIcon = () => {
    if (loading) return <LoadingSpinner size="xl" />;
    
    switch (paymentStatus?.status) {
      case 'success':
        return <CheckCircle size={64} className="text-green-500" weight="duotone" />;
      case 'expired':
      case 'error':
        return <Warning size={64} className="text-red-500" weight="duotone" />;
      case 'timeout':
        return <Clock size={64} className="text-yellow-500" weight="duotone" />;
      default:
        return <LoadingSpinner size="xl" />;
    }
  };
  
  const getStatusTitle = () => {
    if (loading) return 'Processing Payment...';
    
    switch (paymentStatus?.status) {
      case 'success':
        return 'PAYMENT SUCCESSFUL!';
      case 'expired':
        return 'PAYMENT EXPIRED';
      case 'error':
        return 'PAYMENT ERROR';
      case 'timeout':
        return 'VERIFICATION TIMEOUT';
      default:
        return 'CHECKING PAYMENT...';
    }
  };
  
  const getStatusColor = () => {
    if (loading) return 'text-primary';
    
    switch (paymentStatus?.status) {
      case 'success':
        return 'text-green-500';
      case 'expired':
      case 'error':
        return 'text-red-500';
      case 'timeout':
        return 'text-yellow-500';
      default:
        return 'text-primary';
    }
  };
  
  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="payment-success-page">
      <Card className="w-full max-w-md bg-card border-border">
        <CardContent className="text-center py-12 space-y-6">
          <div className="flex justify-center">
            {getStatusIcon()}
          </div>
          
          <div>
            <h1 className={`text-2xl font-heading mb-2 ${getStatusColor()}`}>
              {getStatusTitle()}
            </h1>
            
            {paymentStatus?.message && (
              <p className="text-muted-foreground mb-4">
                {paymentStatus.message}
              </p>
            )}
            
            {paymentStatus?.amount && (
              <div className="bg-muted/20 rounded-lg p-4 mb-4">
                <p className="text-sm text-muted-foreground">Amount Charged</p>
                <p className="text-2xl font-bold text-primary">${paymentStatus.amount}</p>
              </div>
            )}
            
            {loading && (
              <p className="text-sm text-muted-foreground">
                Please wait while we confirm your payment...
                <br />
                Attempt {attempts + 1} of 10
              </p>
            )}
          </div>
          
          <div className="space-y-2">
            {paymentStatus?.status === 'success' && (
              <Button 
                onClick={() => navigate('/dashboard')}
                className="w-full btn-primary"
                data-testid="back-to-dashboard-btn"
              >
                Back to Dashboard
              </Button>
            )}
            
            {(paymentStatus?.status === 'expired' || paymentStatus?.status === 'error') && (
              <Button 
                onClick={() => navigate('/sessions')}
                className="w-full btn-primary"
                data-testid="try-again-btn"
              >
                Try Again
              </Button>
            )}
            
            {paymentStatus?.status === 'timeout' && (
              <>
                <Button 
                  onClick={() => navigate('/dashboard')}
                  className="w-full btn-primary"
                  data-testid="check-registrations-btn"
                >
                  Check My Registrations
                </Button>
                <Button 
                  onClick={() => navigate('/sessions')}
                  variant="outline"
                  className="w-full"
                >
                  Browse Sessions
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}